import './PartnerPayButton.css';

import {
  FinancialPartnerPayButtonInboundPayload,
  FinancialPartnerPayButtonOutboundPayload,
  MerchantIntegrationErrorResponse,
  SetStateOptions,
  ShippingAddressSelectedEvent,
  ShippingRateSelectedEvent,
  BillingAddressSelectedEvent,
  FinancialPartnerLoadButtonEvent,
  NativeMobileClickEvent,
  CommonClickPayload,
} from 'src/shared/FinancialPartnerPayButtonTypes';
import {
  PayButtonClickedResponse,
  PartnerExpress,
  PartnerExpressFactory,
  StripeCheckoutInterface,
  StripePayButtonInterface,
  ShippingChangedResponse,
  ConfirmDetails,
  ConfirmedResponse,
  PartnerErrorEvent,
  BillingChangedResponse,
  NativeMobileClickEvent as ExternalNativeMobileClickEvent,
  PayButtonMountOptions,
} from '../../../shared/FinancialPartnerExternalPayButtonTypes';
import {InputType} from '../../../shared/types';
import {
  FinancialPartnerChildTypes,
  FinancialPartnerOnBeforePayButtonDisplayResponseEvent,
  isFinancialPartnerOnBeforePayButtonDisplayResponseEvent,
  PayButtonOptions,
} from '../../../shared/FinancialPartnerPayButtonTypes';
import {AppConfig, initialize} from '../../initialize';
import type {RequestFn, SendFn} from '../../setupMessages';

import injectScript, {
  redirectLastOpenedWindow,
  closeLastOpenedWindow,
  getLastOpenedWindow,
  LogInfoFunction,
  refocusLastOpenedWindow,
} from './injectScript';
import {getPartnerSdk} from './partnerExpressButtonSdk';
import {FinancialPartnerIntegrationError} from './FinancialPartnerIntegrationError';
import {getCenteredPopupPosition, formatPopupOptions} from './helpers/popups';
import {MerchantIntegrationError} from './MerchantIntegrationError';
import {MerchantRejectedClickError} from './MerchantRejectedClickError';

export const APP_CONTAINER_ID = 'financial-partner-app-container';

class PartnerPayButton {
  private send: SendFn<FinancialPartnerChildTypes>;

  private request: RequestFn<FinancialPartnerChildTypes>;

  private logInfo: LogInfoFunction;

  private partnerExpress?: PartnerExpress | null;

  constructor(
    send: SendFn<FinancialPartnerChildTypes>,
    request: RequestFn<FinancialPartnerChildTypes>,
    logInfo: LogInfoFunction,
  ) {
    this.send = send;
    this.logInfo = logInfo;
    this.request = async (
      payload: FinancialPartnerPayButtonOutboundPayload,
    ) => {
      const result = await request(payload);
      PartnerPayButton.handleFinancialPartnerIntegrationError(result);
      PartnerPayButton.handleMerchantIntegrationError(result, payload, logInfo);
      return result;
    };
  }

  private static handleFinancialPartnerIntegrationError(
    result: FinancialPartnerPayButtonInboundPayload,
  ) {
    if (result.event === 'PartnerIntegrationErrorResponse') {
      throw new FinancialPartnerIntegrationError(result);
    }
  }

  private static handleMerchantIntegrationError(
    result: FinancialPartnerPayButtonInboundPayload,
    payload: FinancialPartnerPayButtonOutboundPayload,
    logInfo: LogInfoFunction,
  ) {
    if (result.event === 'MerchantIntegrationErrorResponse') {
      if (!result.keepAlive) {
        closeLastOpenedWindow(logInfo);
      }
      throw this.makeError(result, payload);
    }
  }

  private static makeError(
    errorResponse: MerchantIntegrationErrorResponse,
    payload: FinancialPartnerPayButtonOutboundPayload,
  ) {
    if (payload.event === 'Clicked') {
      // The ECE now has a merchant-facing feature to reject clicks with
      // preventDefault(). This error is visible in the browser console. Since
      // it is visible to merchants, do not call it a MerchantIntegrationError.
      return new MerchantRejectedClickError(errorResponse);
    } else {
      return new MerchantIntegrationError(errorResponse);
    }
  }

  public async createAndMount(
    loadButtonEvent: FinancialPartnerLoadButtonEvent,
  ): Promise<void> {
    try {
      await this.createPartnerExpress(loadButtonEvent.payButtonOptions);
      await this.mountButton(loadButtonEvent);
    } catch (error) {
      if (error) {
        const message =
          error instanceof Error ? error.message : JSON.stringify(error);

        this.send({
          event: 'Error',
          error: message,
          errorType: 'stripethirdparty_button_mount',
        });
      }
    }
  }

  private async createPartnerExpress(
    payButtonOptions: PayButtonOptions,
  ): Promise<void> {
    try {
      this.partnerExpress =
        await PartnerPayButton.getPartnerExpressFactory().create({
          payButton: this.createPayButton(),
          payButtonOptions,
          checkout: this.createCheckout(),
          locale: payButtonOptions.locale,
          livemode: payButtonOptions.livemode,
        });
    } catch (err) {
      throw Error(`Error creating partner express. ${err}`);
    }
  }

  /**
   * Opens this exact same page as this script is executing on , but in
   * mode=loading as an interstitial until the partner's page is ready (soon
   * after the `sendClicked` promise returns)
   *
   * We open this interstitial to avoid popup blocking that happens across
   * long-lived cross-frame requests (as may happen for high-latency buyers)
   */
  private static openLoadingPage(): void {
    // Default values from Amazon, about which we have no opinion
    const popupDims = {width: 800, height: 670};
    window.open(
      PartnerPayButton.getLoadingPageUrl(),
      `StripeFinancialPartner-${PartnerPayButton.getFrameId()}`,
      formatPopupOptions({
        ...popupDims,
        ...getCenteredPopupPosition(popupDims),
        toolbar: 'no',
        directories: 'no',
        status: 'no',
      }),
    );
  }

  private createPayButton(): StripePayButtonInterface {
    return {
      sendClicked: async (): Promise<PayButtonClickedResponse> => {
        if (getLastOpenedWindow() === null) {
          // If there isn't a popup yet, then open the loading page before
          // sending cross-frame requests in order to avoid popup blockers
          PartnerPayButton.openLoadingPage();
        }
        const response = await this.request({event: 'Clicked'});
        if (response.event !== 'ClickedResponse') {
          throw this.getResponseError('ClickedResponse', response);
        }

        return Promise.resolve(
          PartnerPayButton.makePayButtonClickedResponse(response),
        );
      },

      sendError: (evt) => this.sendError(evt),
    };
  }

  private static makePayButtonClickedResponse(
    clickPayload: CommonClickPayload,
  ): PayButtonClickedResponse {
    const res: PayButtonClickedResponse = {
      stripeExpressSession: clickPayload.stripeExpressSession,
      stripeExpressSessionSignature: clickPayload.stripeExpressSessionSignature,
      checkoutState: clickPayload.checkoutState,
      options: clickPayload.options,
      displayData: clickPayload.displayData,
    };

    const {typeSpecificData} = clickPayload;
    if (typeSpecificData?.shopPay) {
      res.typeSpecificData = typeSpecificData;
    }

    return res;
  }

  /**
   * @returns
   *   this frame's ID. The ID is automatically provided into search
   *   params by stripethirdparty client on load
   */
  private static getFrameId(): string {
    const queryParams = new URLSearchParams(window.location.search);
    const frameId = queryParams.get('id');
    if (!frameId) {
      throw Error(
        'Could not find frameId in query params. This should never happen.',
      );
    }

    return frameId;
  }

  /**
   * @returns the current stripethirdparty page, in mode=loading where it will only show a spinner.
   */
  private static getLoadingPageUrl(): string {
    const {origin, pathname, search} = window.location;
    const queryParams = new URLSearchParams(search);
    queryParams.set('mode', 'loading');
    const loadingPageUrl = `${origin}${pathname}?${queryParams.toString()}`;

    return loadingPageUrl;
  }

  private static getPartnerExpressFactory(): PartnerExpressFactory {
    const partnerExpressFactory = getPartnerSdk();
    if (!partnerExpressFactory) {
      throw Error('Partner script loaded but no PartnerExpressFactory found');
    }

    return partnerExpressFactory;
  }

  public async sendNativeMobileClicked(
    event: NativeMobileClickEvent,
    respondFn: SendFn<FinancialPartnerChildTypes>,
  ): Promise<void> {
    // We alias `type NativeMobileClickEvent = PayButtonClickedResponse` in FinancialPartnerExternalPayButtonTypes
    // explicitly typing here to avoid future duck typing issues.
    const partnerFacingEvent: ExternalNativeMobileClickEvent =
      PartnerPayButton.makePayButtonClickedResponse(event);
    try {
      await this.partnerExpress?.payButton.sendNativeMobileClicked(
        partnerFacingEvent,
      );
      respondFn({
        event: 'NativeMobileClickResponse',
        isPopupOpen: getLastOpenedWindow() !== null,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : JSON.stringify(error);
      respondFn({
        event: 'Error',
        error: `Error sending native mobile clicked. Message: ${message}`,
        errorType: 'stripethirdparty_native_mobile_clicked',
      });
    }
  }

  private async mountButton(
    loadButtonEvent: FinancialPartnerLoadButtonEvent,
  ): Promise<void> {
    const payButtonContainer = document.getElementById(APP_CONTAINER_ID);
    if (!payButtonContainer) {
      throw Error(`Could not find element with id ${APP_CONTAINER_ID}`);
    }
    payButtonContainer.style.display = 'none';

    const payButtonMountOptions = PartnerPayButton.getPayButtonMountOptions(
      loadButtonEvent,
      payButtonContainer,
    );
    await this.partnerExpress?.payButton.mount(payButtonMountOptions);

    if (payButtonContainer.hasChildNodes()) {
      if ((await this.onBeforePayButtonDisplay())?.shouldDisplayButton) {
        payButtonContainer.style.display = 'block';
      }
    } else if (loadButtonEvent.isNativeMobile) {
      // for native mobile, keep the button hidden.
      await this.onBeforePayButtonDisplay();
    } else {
      throw Error('Partner button mounted but no elements found.');
    }
  }

  private static getPayButtonMountOptions(
    loadButtonEvent: FinancialPartnerLoadButtonEvent,
    element: HTMLElement,
  ): PayButtonMountOptions {
    const isNativeMobile = loadButtonEvent.isNativeMobile || false;
    const {typeSpecificData} = loadButtonEvent;
    return {
      element,
      isNativeMobile,
      typeSpecificData,
    };
  }

  public async setState(opts: SetStateOptions): Promise<void> {
    try {
      await this.partnerExpress?.payButton.setState(opts);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : JSON.stringify(error);
      this.send({
        event: 'Error',
        error: `Error setting button state. Message: ${message}`,
        errorType: 'stripethirdparty_set_button_state',
      });
    }
  }

  public async redirectCheckoutInterface(
    redirectUrl: string,
    respondFn: SendFn<FinancialPartnerChildTypes>,
  ): Promise<void> {
    let redirectedByPartner = false;
    try {
      const onBeforeRedirect = this.partnerExpress?.checkout?.onBeforeRedirect;
      if (onBeforeRedirect) {
        const resp = await onBeforeRedirect({redirectUrl});
        redirectedByPartner = resp?.redirectedByPartner || false;
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : JSON.stringify(error);
      this.send({
        event: 'Error',
        error: `Error redirecting checkout interface. Message: ${message}`,
        errorType: 'stripethirdparty_redirect_checkout_interface',
      });
    }

    respondFn({
      event: 'RedirectCheckoutInterfaceResponse',
      redirectedByPartner,
    });
  }

  private async onBeforePayButtonDisplay(): Promise<FinancialPartnerOnBeforePayButtonDisplayResponseEvent | void> {
    const response = await this.request({
      event: 'OnBeforePayButtonDisplay',
    });

    if (isFinancialPartnerOnBeforePayButtonDisplayResponseEvent(response)) {
      return response;
    } else {
      throw this.getResponseError('OnBeforePayButtonDisplayResponse', response);
    }
  }

  private async billingAddressSelected(
    event: BillingAddressSelectedEvent,
  ): Promise<BillingChangedResponse> {
    const response = await this.request(event);
    if (response.event !== 'BillingChangedResponse') {
      throw this.getResponseError('BillingChangedResponse', response);
    }

    return Promise.resolve({
      checkoutState: response.checkoutState,
      error: response.error,
    });
  }

  private async shippingSelected(
    event: ShippingAddressSelectedEvent | ShippingRateSelectedEvent,
  ): Promise<ShippingChangedResponse> {
    const response = await this.request(event);
    if (response.event !== 'ShippingChangedResponse') {
      throw this.getResponseError('ShippingChangedResponse', response);
    }

    return Promise.resolve({
      merchantDecision: response.merchantDecision,
      checkoutState: response.checkoutState,
      error: response.error,
    });
  }

  private async readyForBuyerInteraction(): Promise<void> {
    this.send({event: 'ReadyForBuyerInteraction'});
    return Promise.resolve();
  }

  private async confirm(details: ConfirmDetails): Promise<ConfirmedResponse> {
    const response = await this.request({event: 'Confirmed', ...details});
    if (response.event !== 'ConfirmedResponse') {
      throw this.getResponseError('ConfirmedResponse', response);
    }
    if (!response.error) {
      // close popup only if there is no error
      closeLastOpenedWindow(this.logInfo);
    }
    return Promise.resolve({
      status: response.status,
      error: response.error,
    });
  }

  private async canceled(): Promise<void> {
    const windowClosed = closeLastOpenedWindow(this.logInfo);
    if (windowClosed) {
      this.send({event: 'Canceled'});
    }
    return Promise.resolve();
  }

  // eslint-disable-next-line class-methods-use-this
  private getResponseError = (
    expectedEvent: string,
    response: InputType<FinancialPartnerChildTypes>,
  ) => Error(`Expected ${expectedEvent} but got ${response?.event}`);

  // Remove typescript warnings for this stub which will be replaced in
  // BPUX-3041
  //
  // eslint-disable-next-line class-methods-use-this
  private sendError(evt?: PartnerErrorEvent): void {
    this.send({
      event: 'FatalErrorEvent',
      message: evt?.message,
      keepAlive: evt?.keepAlive,
    });
    if (!evt?.keepAlive) {
      closeLastOpenedWindow(this.logInfo);
    }
  }

  private createCheckout(): StripeCheckoutInterface {
    return {
      sendReadyForBuyerInteraction: () => this.readyForBuyerInteraction(),
      sendBillingAddressSelected: (event) =>
        this.billingAddressSelected({
          event: 'BillingAddressSelected',
          ...event,
        }),
      sendShippingAddressSelected: (event) =>
        this.shippingSelected({event: 'ShippingAddressSelected', ...event}),
      sendShippingRateSelected: (event) =>
        this.shippingSelected({event: 'ShippingRateSelected', ...event}),
      sendConfirmed: (details) => this.confirm(details),
      sendCanceled: () => this.canceled(),
      sendError: (evt) => this.sendError(evt),
    };
  }
}

const initializeButton = ({
  scriptUrl,
  loadButtonEvent,
  send,
  request,
  setPartnerButton,
  logInfo,
}: {
  scriptUrl: string;
  loadButtonEvent: FinancialPartnerLoadButtonEvent;
  send: SendFn<FinancialPartnerChildTypes>;
  request: RequestFn<FinancialPartnerChildTypes>;
  setPartnerButton: (partnerButton: PartnerPayButton) => void;
  logInfo: LogInfoFunction;
}): void => {
  const onLoadCallback = async () => {
    const partnerPayButton = new PartnerPayButton(send, request, logInfo);
    await partnerPayButton.createAndMount(loadButtonEvent);
    setPartnerButton(partnerPayButton);
  };

  const onErrorCallback: OnErrorEventHandlerNonNull = (
    event,
    source?,
    lineno?,
    colno?,
    error?,
  ) => {
    send({
      event: 'Error',
      error: 'Error loading partner script',
      errorType: 'stripethirdparty_script_load',
      eventType: typeof event === 'string' ? event : event.type,
      errorObj: error,
      source,
      lineno,
      colno,
    });
  };

  const onCanceledCallback = () => {
    if (getLastOpenedWindow() !== null) {
      send({event: 'Canceled'});
    }
  };

  window.addEventListener('focus', () => {
    send({event: 'onFocus'});
  });

  window.addEventListener('blur', () => {
    send({event: 'onBlur'});
  });

  injectScript(
    scriptUrl,
    onLoadCallback,
    onErrorCallback,
    onCanceledCallback,
    logInfo,
  );
};

export const initializeFinancialPartnerPayButtonMode = (args: {
  partnerExpressUrl: string;
}): void => {
  const app = (config: AppConfig<FinancialPartnerChildTypes>) => {
    let partnerButtonResolve: (x: null) => void;
    const partnerButtonPromise: Promise<null> = new Promise((resolve) => {
      partnerButtonResolve = resolve;
    });
    let partnerButton: PartnerPayButton;

    const logInfo: LogInfoFunction = (message, data) => {
      config.send({event: 'Info', message, data});
    };

    config.subscribe(async (messageData, respondFn) => {
      switch (messageData.event) {
        case 'loadButton':
          initializeButton({
            scriptUrl: args.partnerExpressUrl,
            loadButtonEvent: messageData,
            send: config.send,
            request: config.request,
            logInfo,
            setPartnerButton: (button: PartnerPayButton) => {
              // Store the button in a variable so loadButton can be called
              // multiple times. If we stored directly in the promise, this
              // would lead to strange behavior when the promise is resolved the
              // second time as the calls below would use the old button.
              partnerButton = button;
              partnerButtonResolve(null);
            },
          });
          break;
        case 'setButtonState': {
          await partnerButtonPromise;
          partnerButton.setState(messageData.options);
          break;
        }
        case 'NativeMobileClicked': {
          await partnerButtonPromise;
          await partnerButton.sendNativeMobileClicked(messageData, respondFn);
          break;
        }
        case 'RedirectCheckoutInterface': {
          if (messageData.bypassPartnerRedirect) {
            redirectLastOpenedWindow(messageData.authenticationUrl);
          } else {
            await partnerButtonPromise;
            partnerButton.redirectCheckoutInterface(
              messageData.authenticationUrl,
              respondFn,
            );
          }
          break;
        }
        case 'SessionExpired': {
          closeLastOpenedWindow(logInfo);
          break;
        }
        case 'abort': {
          closeLastOpenedWindow(logInfo);
          break;
        }
        case 'refocus': {
          refocusLastOpenedWindow();
          break;
        }
        default:
          break;
      }
    });
  };
  initialize({app, owner: 'lpmxp'});
};
